import { clsx } from 'clsx';
import { FC, InputHTMLAttributes, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { EMAIL_REGEX } from 'src/constants/regex.constants';
import { useRecaptcha } from 'src/general/helpers/useRecaptcha';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { validateEmail } from '../../../../services/AuthService';
import { Button } from '../../Button/Button';
import { LoadingIndicator } from '../../LoadingIndicator/LoadingIndicator';
import { InputBase } from '../Input/Input.type';
import { FieldWrapper } from './atoms/FieldWrapper';
import { getInputAria } from './Input.helpers';
import styles from './InputEmail.module.scss';

const DEFAULT_INVALID_ERROR_MESSAGE = 'Please enter a valid email address';

type Props = Prettify<
  InputBase<string> &
    Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type'> & {
      onChange: (value: string) => void;
      showClientError?: boolean;
      pending?: boolean;
    }
>;

export const InputEmail: FC<Props> = ({
  disabled,
  defaultValue,
  value,
  onChange,
  placeholder,
  labelOutside,
  subLabel,
  showClientError,
  pending,
  ...props
}) => {
  const { isLoadingRecaptcha, getRecaptchaToken } = useRecaptcha();
  const [tempValue, setTempValue] = useState(`${defaultValue ?? ''}`);
  const [debouncedValue, setDebouncedValue] = useState('');
  const [validationRequired, setValidationRequired] = useState(false);
  const [suggestedEmail, setSuggestedEmail] = useState<string | undefined>(undefined);
  const [isValidating, setIsValidating] = useState(false);
  const [clientError, setClientError] = useState(props.error);
  useDebounce(
    () => {
      setDebouncedValue(tempValue);
      const res = validateSchema(tempValue);
      if (res.success) {
        setValidationRequired(true);
      }
    },
    500,
    [tempValue],
  );

  useEffect(() => {
    const validate = async () => {
      if (!validationRequired || !debouncedValue) return;
      let recaptchaToken;
      if (!isLoadingRecaptcha) {
        recaptchaToken = await getRecaptchaToken('validate_email');
      }
      setIsValidating(true);
      try {
        const res = await validateEmail(debouncedValue, { recaptchaToken });
        !res.valid && setSuggestedEmail(res.suggestion);
        if (res.suggestion || !showClientError) {
          setClientError('');
        } else if (res.block) {
          setClientError(DEFAULT_INVALID_ERROR_MESSAGE);
        }
        pushToDataLayer({
          event: 'validate_email',
          suggestion: !!res.suggestion ? 'true' : 'false',
          blocked: res.block ? 'true' : 'false',
        });
      } catch (error) {
        console.log(error);
      }
      setIsValidating(false);
    };
    validate();
  }, [validationRequired, debouncedValue, showClientError]);

  useEffect(() => {
    if (!pending) {
      setClientError(props.error);
    }
  }, [pending, props.error]);

  const resetValidation = () => {
    setValidationRequired(false);
    setSuggestedEmail(undefined);
    setClientError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(e.target.value);
    onChange(e.target.value);
    resetValidation();
  };

  const handleOnClickSuggestedEmail = () => {
    onChange(suggestedEmail ?? '');
    setTempValue(suggestedEmail ?? '');
    pushToDataLayer({
      event: 'validate_email_click',
    });
    resetValidation();
  };

  return (
    <div className={clsx(styles.root, props.className)} data-testid="email-input-form">
      <FieldWrapper
        {...props}
        disabled={disabled}
        className={styles.emailInput}
        error={clientError}
        labelOutside={labelOutside}
        subLabel={subLabel}
      >
        <input
          {...props}
          {...getInputAria(props)}
          onChange={handleInputChange}
          type="email"
          value={tempValue}
          disabled={disabled}
          placeholder={placeholder ?? ' '} // placeholder must to be " " when nullish to avoid ios styling issues
          data-testid="email-visual-input"
          name={`${props.name}-visual`}
        />
        {isValidating && (
          <div className={styles.inputIndicator} data-testid="loading-validate-email">
            <LoadingIndicator />
          </div>
        )}
      </FieldWrapper>
      <input
        {...getInputAria(props)}
        name={props.name}
        value={value}
        onChange={() => {}}
        className={styles.hiddenInput}
        data-testid="email-input"
        type="hidden"
      />
      {suggestedEmail && validationRequired && !clientError && (
        <span data-testid="email-suggestion" className={styles.helperText}>
          {`Did you mean `}
          <Button
            onClick={handleOnClickSuggestedEmail}
            className={styles.suggestedEmail}
            data-testid="suggested-email"
            type="button"
            variant="link"
          >
            <span data-testid="email-suggestion-text">{suggestedEmail}</span>
          </Button>
          {`?`}
        </span>
      )}
    </div>
  );
};

const validateSchema = (schema: any) => {
  if (typeof schema === 'string') {
    return {
      success: !!schema.match(EMAIL_REGEX),
    };
  }

  return { success: false };
};
